/**
 * Product card color swatches — hover previews on desktop; click/tap pins a color
 * until another swatch is chosen. Card title/image still link to the PDP.
 */
(function () {
  'use strict';

  var PREVIEW_CLASS = 'maxynn-card--swatch-preview';
  var SWATCH_SELECTOR = '.maxynn-card__swatch[data-variant-image]';
  var hasFinePointer =
    window.matchMedia && window.matchMedia('(hover: hover)').matches;
  var preloadedUrls = new Set();
  var preloadObserver = null;
  var pinnedByContainer = new WeakMap();

  function getCardWrapper(el) {
    return el.closest('.card-wrapper');
  }

  function getCardImage(card) {
    if (!card) return null;
    return card.querySelector('.card__media .media img:first-child');
  }

  function rememberOriginal(img) {
    if (img.dataset.maxynnOriginalSrc !== undefined) return;
    img.dataset.maxynnOriginalSrc = img.currentSrc || img.getAttribute('src') || '';
    img.dataset.maxynnOriginalSrcset = img.getAttribute('srcset') || '';
  }

  function preloadVariantImage(swatch) {
    var src = swatch.getAttribute('data-variant-image');
    if (!src || preloadedUrls.has(src)) return;
    preloadedUrls.add(src);
    var loader = new Image();
    var srcset = swatch.getAttribute('data-variant-srcset');
    if (srcset) loader.srcset = srcset;
    loader.src = src;
  }

  function preloadContainerImages(container) {
    if (container.dataset.maxynnPreloaded === 'true') return;
    container.dataset.maxynnPreloaded = 'true';
    var swatches = container.querySelectorAll(SWATCH_SELECTOR);
    for (var i = 0; i < swatches.length; i++) preloadVariantImage(swatches[i]);
  }

  function observeForPreload(container) {
    if (!('IntersectionObserver' in window)) {
      preloadContainerImages(container);
      return;
    }
    if (!preloadObserver) {
      preloadObserver = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (!entries[i].isIntersecting) continue;
            preloadContainerImages(entries[i].target);
            preloadObserver.unobserve(entries[i].target);
          }
        },
        { rootMargin: '400px 0px' }
      );
    }
    preloadObserver.observe(container);
  }

  function applyImage(img, src, srcset) {
    rememberOriginal(img);
    img.removeAttribute('srcset');
    img.setAttribute('src', src);
    if (srcset) {
      img.setAttribute('srcset', srcset);
    }
    if (img.decode) {
      img.decode().catch(function () {});
    }
  }

  function restoreImage(img) {
    if (!img || img.dataset.maxynnOriginalSrc === undefined) return;
    img.removeAttribute('srcset');
    img.setAttribute('src', img.dataset.maxynnOriginalSrc);
    if (img.dataset.maxynnOriginalSrcset) {
      img.setAttribute('srcset', img.dataset.maxynnOriginalSrcset);
    }
  }

  function setActiveSwatch(swatch) {
    var siblings = swatch.parentElement.querySelectorAll('.maxynn-card__swatch');
    for (var i = 0; i < siblings.length; i++) {
      siblings[i].classList.toggle('is-active', siblings[i] === swatch);
    }
  }

  function clearActiveSwatches(container) {
    var active = container.querySelectorAll('.maxynn-card__swatch.is-active');
    for (var i = 0; i < active.length; i++) active[i].classList.remove('is-active');
  }

  function getPinnedSwatch(container) {
    return pinnedByContainer.get(container) || null;
  }

  function pinSwatch(container, swatch) {
    pinnedByContainer.set(container, swatch);
    preview(swatch);
  }

  function restorePinnedOrClear(card, container) {
    var pinned = getPinnedSwatch(container);
    if (pinned) {
      preview(pinned);
      return;
    }
    clearPreview(card);
  }

  function preview(swatch) {
    var card = getCardWrapper(swatch);
    var img = getCardImage(card);
    if (!img) return;

    var src = swatch.getAttribute('data-variant-image');
    if (!src) return;

    preloadVariantImage(swatch);
    card.classList.add(PREVIEW_CLASS);

    if (img.getAttribute('src') === src && card.classList.contains(PREVIEW_CLASS)) {
      setActiveSwatch(swatch);
      return;
    }

    applyImage(img, src, swatch.getAttribute('data-variant-srcset'));
    setActiveSwatch(swatch);
  }

  function clearPreview(card) {
    if (!card) return;
    var img = getCardImage(card);
    var container = card.querySelector('.maxynn-card__swatches');
    card.classList.remove(PREVIEW_CLASS);
    restoreImage(img);
    if (container) clearActiveSwatches(container);
  }

  function handleSwatchActivate(e) {
    var swatch = e.target.closest(SWATCH_SELECTOR);
    if (!swatch) return false;
    e.preventDefault();
    e.stopPropagation();
    var container = swatch.closest('.maxynn-card__swatches');
    if (container) pinSwatch(container, swatch);
    else preview(swatch);
    return true;
  }

  function bindSwatchContainer(container) {
    if (container.dataset.maxynnSwatchesBound === 'true') return;
    container.dataset.maxynnSwatchesBound = 'true';

    container.addEventListener('pointerdown', function (e) {
      if (handleSwatchActivate(e)) return;
      var swatch = e.target.closest('.maxynn-card__swatch');
      if (!swatch) return;
      e.preventDefault();
      e.stopPropagation();
    });

    container.addEventListener('click', function (e) {
      handleSwatchActivate(e);
    });

    observeForPreload(container);
    container.addEventListener(
      'pointerenter',
      function () {
        preloadContainerImages(container);
      },
      { once: true, passive: true }
    );

    container.addEventListener('pointerover', function (e) {
      if (!hasFinePointer) return;
      var swatch = e.target.closest(SWATCH_SELECTOR);
      if (swatch && container.contains(swatch)) preview(swatch);
    });

    container.addEventListener('pointerleave', function () {
      if (!hasFinePointer) return;
      restorePinnedOrClear(getCardWrapper(container), container);
    });

    container.addEventListener('focusin', function (e) {
      var swatch = e.target.closest(SWATCH_SELECTOR);
      if (swatch && container.contains(swatch)) preview(swatch);
    });

    container.addEventListener('focusout', function (e) {
      if (container.contains(e.relatedTarget)) return;
      if (!hasFinePointer) return;
      restorePinnedOrClear(getCardWrapper(container), container);
    });
  }

  function initSwatches(root) {
    var scope = root || document;
    var containers = scope.querySelectorAll('.maxynn-card__swatches');
    for (var i = 0; i < containers.length; i++) bindSwatchContainer(containers[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initSwatches(); });
  } else {
    initSwatches();
  }

  document.addEventListener('shopify:section:load', function (e) {
    initSwatches(e.target);
  });
})();
